/**
 * Just this class and friends
 * @module sample-graph
 */
/**
* Sample Graph
*
* A graphing engine that aims to plot sampled data.
*
* Features:
*   - Time-based or simple numeric x-axis
*   - Label points and sections TODO
*   - Linear interpolation TODO
*
* Design decisions:
*   - All pixel coordinates passed to functions are relative to the
* canvas origin. The plot probably has a margin.
*
* What's a pointsAround object? {closestI, lowI, highI, pct}
* lets say we have this:
*   4---------5------x--6---------7
*   lowI: 5, highI: 6, closestI: 6, pct: 0.8
*
* @class SampleGraph
*/
/** @namespace SampleGraph */
(function () {
  // SampleGraph constructor
  // Required options: canvasId, data
  var SG = function (opts) {
    _.bindAll(this, 'zoomOutOnClick', 'zoomToDataSlice');
    this.setupGlobalEvents();

    // Configure graph from options
    this.canvasId = opts.canvasId;
    this.uniqueClass = opts.canvasId + '_sg';
    this.$canvas = $('#' + this.canvasId);
    this.canvas = this.$canvas.get(0);
    this.ctx = document.getElementById(this.canvasId).getContext("2d");
    this.$canvas.addClass('sample-graph');
    this.options = _.clone(SG.defaultOptions);
    this.trigger('configure');
    _.extend(this.options, opts);
    this.data(opts.data || []);
    this._xAxisType = opts.xAxisType || 'distance';
    this.metricsEnabled([]);
    this.nearestPixel = false;

    // maybe this should be per metric since it may only need to apply
    // to ele
    this.minPaddingPct = 0.05;

    this.defaultStrokeStyle = 'black';
    this.defaultFillStyle = 'black';

    this.setInitialCanvasState();

    // setting padding for plot region
    this.plotMargin({
      top: 7
    });

    this.trigger('initialize:after');
    this.render();
  };

  // allow setting / triggering of events for all instances
  _.extend(SG, Backbone.Events);

  _.extend(SG.prototype, Backbone.Events, {
    bindTo: function(target, ev, callback) {
      target.bind(ev, callback);
      var calls = this._externalBindings || (this._externalBindings = {});
      var list  = calls[ev] || (calls[ev] = []);
      list.push([target, callback]);
    },
    unbindFrom: function(target, ev, callback) {
      target.unbind(ev, callback);
      var calls = this._externalBindings;
      if (!calls) return this;
      var list = calls[ev];
      if (!list) return this;
      for (var i = 0, l = list.length; i < l; i++) {
        if (list[i] && target === list[i][0] && callback === list[i][1]) {
          list[i] = null;
          break;
        }
      }
    },
    unloadBindings: function() {
      this.trigger('unload_bindings:before');
      _.each(this._externalBindings, function(list, ev) {
        _.each(list, function(arr) {
          if(arr)
            arr[0].unbind(ev, arr[1]);
        })
      });
      this._externalBindings = {};
    },
    /**
    * Set a graph option. Does not re-render or anything, just sets the option. Check out SampleGraph.defaultOptions to see them all.
    * @method set
    * @param {String | Object} key As a string, specifies the property to set. Otherwise, expects an object with property/value pairs for mass assignment.
    * @param {MIXED} val (optional, only use if passing a single string key) The value to assign
    */
    set: function(key, val) {
      if(key && val) {
        this.options[key] = val;
      } else{
        // only 1 param, expected to be an object with multiple properties to set
        _.extend(this.options, key);
      }
    },
    /**
    * Get a graph option.
    * @method get
    * @param {String} key The property to get
    * @return {MIXED} The value associated with the specified property
    */
    get: function(key) {
      return this.options[key];
    },

    // allow binding to events triggered from any instance
    // used for hooking into initialize:after by plugins
    setupGlobalEvents: function() {
      this.bind('all', function() {
        var argsWithoutEv = Array.prototype.slice.call(arguments, 1);
        SG.trigger.apply(SG, [arguments[0], this].concat(argsWithoutEv));
      }, this);
    },

    // it will be ele if its enabled, because thats how its sorted
    primaryMetric: function() {
      var metricsEnabled = this.metricsEnabled()
      if (metricsEnabled.length === 0) return
      return this.metrics(metricsEnabled[0]);
    },

    // setter/getter for x-axis type
    // typical values: "time" or "distance"
    // also valid: a key referencing a monotonically increasing value that exists on all data points
    xAxisType: function(str) {
      if(str && str != this._xAxisType) {
        this._xAxisType = str;
        this.trigger('xAxisType:change');
      }
      return this._xAxisType;
    },

    strokeStyle: function(metric) {
      return this.options.strokes[metric];
    },

    fillStyle: function(metric) {
      if (metric === 'unpaved') {
        return this.surfacePattern()
      }
      return this.options.fills[metric];
    },

    // setter / getter for plot margins - keys are top bottom left right
    // updates plotWidth and plotHeight
    plotMargin: function(margin) {
      if(margin) {
        if(!this._plotMargin) this._plotMargin = {top:0,right:0,bottom:0,left:0};
        _.extend(this._plotMargin, margin);
        this.plotWidth = this.width() - 1 - this._plotMargin.left - this._plotMargin.right;
        this.plotHeight = this.height() - 1 - this._plotMargin.top - this._plotMargin.bottom;
      }
      return this._plotMargin;
    },

    // overall width
    width: function() {
      return this.$canvas.width();
    },

    // overall height
    height: function() {
      return this.$canvas.height();
    },

    showSurface: function(bool) {
      if (bool !== undefined) {
        this._showSurface = bool;
        return
      }
      if (this._showSurface === undefined) {
        let val = this.options.showSurface;
        if (val === undefined) { val = Routes.activeMap && Routes.activeMap.activeRoute.showSurface }
        if (val === undefined) { this.options.segmentSelectorMode === 'planner' || this.metricsCalculator().hasSurface() }
        this._showSurface = val
      }
      return this._showSurface
    },

    toggleShowSurface: function () {
      this.showSurface(!this.showSurface());
    },

    // resize the canvas and re-render the graph
    resize: function(w, h) {
      if (!w) w = this.$canvas.parent().width()
      if (!h) h = this.$canvas.parent().height()
      this.canvas.width = w;
      var sgBarHeight = this.options.plotOnly
        ? 0
        : $('.sg-bar').height() || 25; // default to 25px if it's not loaded yet, happens in routes/embed
      this.canvas.height = h - sgBarHeight;
      this.$canvas.offset({top: this.$canvas.parent().offset().top + $('.sg-bar').height()});
      // after changing width and height it resets the canvas
      this.setInitialCanvasState();
      this.render(true);
      this.trigger('resize');
    },

    setInitialCanvasState: function() {
      this.ctx.translate(0.5, 0.5);
      this.ctx.strokeStyle = this.defaultStrokeStyle;
      this.ctx.fillStyle = this.defaultFillStyle;
      this.ctx.save();
    },

    // are we zoomed in?
    zoomedIn: function() {
      return !! this.zoomedSlice();
    },

    zoomedSlice: function(dataSlice, globalNotificationDone) {
      if(dataSlice) {
        if(SG.globalOptions.globalZoom) {
          // make sure this is only bound once, so unbind if its been done
          this.unbindFrom(this, 'pointselected', this.zoomOutOnClick);
          this.bindTo(this, 'pointselected', this.zoomOutOnClick);
          // dont propogate this change out globally if it has already
          // been done
          if(!globalNotificationDone)  {
            this._data._zoomedSlice = dataSlice;
            this.unbindFrom(this._data, 'zoom:in', this.zoomToDataSlice);
            this._data.trigger('zoom:in', dataSlice, true);
            this.bindTo(this._data, 'zoom:in', this.zoomToDataSlice);
          }
        } else {
          if(!this._zoomedSlice) // only need to bind this if we're not already zoomed in
            this.bindTo(this, 'pointselected', this.zoomOutOnClick);
          this._zoomedSlice = dataSlice;
        }

      }
      return SG.globalOptions.globalZoom ? this._data._zoomedSlice : this._zoomedSlice;
    },

    // keep track of the zoomed slice and render
    zoomToDataSlice: function(dataSlice, globalNotificationDone) {
      this.zoomedSlice(dataSlice, globalNotificationDone);
      this.trigger('zoom:in', dataSlice);
      this.render();
    },

    // zoom out and unbind this listener
    zoomOutOnClick: function(pointsAround, globalNotificationDone) {
      this.zoomOut(globalNotificationDone);
      this.unbindFrom(this, 'pointselected', this.zoomOutOnClick);
    },

    zoomOut: function(globalNotificationDone) {
      if(SG.globalOptions.globalZoom && !globalNotificationDone) {
        delete this._data._zoomedSlice;
        this.unbindFrom(this._data, 'zoom:out', this.zoomOutOnClick);
        this._data.trigger('zoom:out', null, true);
        this.bindTo(this._data, 'zoom:out', this.zoomOutOnClick);
      } else if(this._zoomedSlice) {
        delete this._zoomedSlice;
      }
      this.trigger('zoom:out');
      this.render();
    },

    // choose which metrics to graph and re-render
    // typical: ['ele', 'hr'] or ['watts']
    // valid: array of keys that exists on any (not necessarily on all) data points
    metricsEnabled: function(metrics) {
      if (metrics) {
        if(!this.plotMetaData) this.plotMetaData = {time:{}, distance:{}};
        _.each(metrics, function(metric) {
          if(!this.plotMetaData[metric])
            this.plotMetaData[metric] = {};
        }, this);
        if (metrics.length > 0 && this.showSurface() && this._metricsEnabled.indexOf('surfaces') === -1) {
          // surface is a fake metric, so we have to add it manually
          metrics.push('surfaces');
        }
        var sortBy = {
          ele: 1,
          surfaces: 2,
          cad: 3,
          hr: 4,
          speed: 5,
          watts: 6,
          grade: 7
        }
        this._metricsEnabled = _.sortBy(metrics, function(x) { return sortBy[x] });
        // metricsEnabled changed so blow the metricVariationInUse cache
        this._metricVariationInUse = {};
      }
      return this._metricsEnabled
    },

    // hand back the base metric unless a computed version is in
    // metricsEnabled
    metricVariationInUse: function(baseMetricKey) {
      // we cache this value until metricsEnabled changes, at which
      // time metricsEnabled will blow the cached val
      if(this._metricVariationInUse[baseMetricKey])
        return this._metricVariationInUse[baseMetricKey];

      // loop through enabled, if one of them is a computed variation,
      // return that key. otherwise hand back the baseMetricKey
      var computedKey = _.find(this.metricsEnabled(), function(key) {
        return this.metrics(key).isComputed() && this.metrics(key).baseMetricKey() == baseMetricKey;
      }, this);
      return computedKey || baseMetricKey;
    },

    // what's a pointsAround object?
    // lets say we have this:
    //   4---------5------x--6---------7
    //   lowI: 5, highI: 6, closestI: 6, pct: 0.8
    pointsAroundToX: function(pointsAround) {
      var data = this.data(), xAxisType = this.xAxisType();
      if(pointsAround.highI > data.length-1) return; // out of bounds
      // first scale based on pct to get the interpolated x value
      var delta = data[pointsAround.highI][xAxisType] - data[pointsAround.lowI][xAxisType];
      var interpolatedValue = data[pointsAround.lowI][xAxisType] + delta * pointsAround.pct;
      return this.plotToPixel(interpolatedValue);
    },

    // type can by pixel, time, or distance
    pointsAroundX: function(x, type) {
      var data = this.dataToRender(),
          ptOffset, ptsAround;
      if(this.zoomedIn()) ptOffset = this.zoomedSlice().i;

      if(type == 'pixel') {
        ptsAround = this._data.pointsAroundX(this.pixelToPlot(x), this.xAxisType(), data, ptOffset);
        ptsAround.x = x;
      } else {
        ptsAround = this._data.pointsAroundX(x, type, data, ptOffset);
      }
      return ptsAround;
    },

    // given a canvas x coord, get the index of the nearest point
    canvasXToDataI: function(x) {
      return this.pointsAroundX(x, 'pixel');
    },

    // pass a pixel, get back a scaled plot value
    // passing x or y alone will get a number back. passing both will get an object back with x and y properties
    pixelToPlot: function(x, y, metric) {
      var plotX, plotY;
      if(x) {
        x -= this._plotMargin.left;
        plotX = x / this.xRatio + this.xMin;
      }
      if(y) {
        y = this.plotHeight - (y - this._plotMargin.top);
        plotY = y / this.plotMetaData[metric].ratio + this.plotMetaData[metric].min;
      }
      if(!_.isNumber(x)) return plotY;
      if(!_.isNumber(y)) return plotX;
      return {x: plotX, y: plotY};
    },

    plotToPixel: function(x, y, metric) {
      var pixelX, pixelY, round=this.nearestPixel;
      if(x || x==0) {
        pixelX = ((x - this.xMin) * this.xRatio + this._plotMargin.left);
        if(round) pixelX = Math.round(pixelX);
      }
      if(y || y==0) {
        pixelY = ((this.plotHeight - (y - this.plotMetaData[metric].min) * this.plotMetaData[metric].ratio) + this._plotMargin.top);
        if(round) pixelY = Math.round(pixelY);
      }
      if (isNaN(pixelX)) pixelX = 0
      if (isNaN(pixelY)) pixelY = 0
      if(!x && x!=0) return pixelY;
      if(!y && y!=0) return pixelX;
      return {x: pixelX, y: pixelY};
    },

    /**
     * Setter/getter for the data used by this graph. When used as a setter fires data:change event. Also binds to points:change on the Data instance, and fires a data:change event in response.
     * @param {rwgps.Data | Array} data If a SampleGraph.Data instance is provided, just uses that. If an Array is provided, creates a SampleGraph.Data instance from it.
     * @return {Array} The points only - not the rwgps.Data instance.
     */
    data: function(data) {
      if(data) {
        var alreadyBoundToEvents = false;
        if(this._data && this._data === data) alreadyBoundToEvents = true;

        if(data.points && data.metricsCalculator) {
          // looks like an instance of rwgps.Data
          this._data = data;
          // lets see if its already zoomed in, if so we gotta track this ourselves and bind a handler for zoom out
          if(data._zoomedSlice) {
            this.zoomedSlice(data._zoomedSlice, true);
            this.trigger('zoom:in', data._zoomedSlice);
          }
        } else {
          if(this._data)
            this._data.points(data);
          else
            this._data = new rwgps.Data(data);
        }
        var metricsX = this.metricsX();
        if(metricsX.length == 1 && metricsX[0].key() != this.xAxisType()) {
          this.xAxisType(metricsX[0].key());
        }
        if(!alreadyBoundToEvents) {
          // in case we're using globalZoom, we'll need to be bound to
          // the zoom events
          this.bindTo(this._data, 'zoom:out', this.zoomOutOnClick);
          this.bindTo(this._data, 'zoom:in', this.zoomToDataSlice);
          // if the Data instance changes underneath us... forward those events along!
          this.bindTo(this._data, 'points:change', function() { this.trigger('data:change') }, this);
        }
        // data changed, so update metrics
        this.resetMetrics();
        // need to trigger a change event right now as well.
        this.trigger('data:change');
      }

      return this._data.points();
    },

    resetMetrics: function() {
      delete this._metrics;
      var metricsEnabled = _.filter(this.metricsEnabled(), function(key) { return this.metrics(key) }, this);
      if(metricsEnabled.length === 0) {
        if (this.metrics('ele')) {
          metricsEnabled.push('ele');
          if (this.options.segmentSelectorMode === 'planner' || this.showSurface()) {
            metricsEnabled.push('surfaces');
          }
        } else if (this.metricsY().length > 0) {
          if (this.metricsY()[0].key() !== 'surfaces') { // don't ever want an initial render with metricsEnabled === ['surfaces']
            metricsEnabled.push(this.metricsY()[0].key());
          }
        }
      }
      this.metricsEnabled(metricsEnabled);

      // make sure xaxis is still available, otherwise use what is
      if(!this.metrics(this.xAxisType())) {
        if(this.metricsX().length > 0)
          this.xAxisType(this.metricsX()[0].key());
      }
    },

    // if we're zooming in, and we've got a selection, render that. otherwise grab all data
    dataToRender: function() {
      if(this.zoomedIn()) {
        return this.zoomedSlice().points();
      } else {
        return this.data();
      }
    },
    /**
     * Delegates to our instance of Data.
     * @return {rwgps.MetricsCalculator} Current metrics calculator
     */
    metricsCalculator: function() {
      return this._data.metricsCalculator();
    },


    // this method name is long...
    metricsCalculatorForDataToRender: function() {
      if(this.zoomedIn()) {
        return this.zoomedSlice().metricsCalculator();
      } else {
        return this.metricsCalculator();
      }
    },

    // includes computed variations
    metricsKeys: function() {
      return _.map(this.metricsWithComputed(), function(m) { return m.key() });
    },

    metricsY: function() {
      return _.filter(this.metrics(), function(m) { return m.axis() == 'y'});
    },

    metricsX: function() {
      return _.filter(this.metrics(), function(m) { return m.axis() == 'x'});
    },

    metricsKeysY: function() {
      return _.map(this.metricsY(), function(m) { return m.key() });
    },


    metricsWithComputed: function() {
      return this.metrics('all');
    },

    // allows special key "all" to include computed
    metrics: function(key) {
      var mc = this.metricsCalculatorForDataToRender();
      if(!this._metrics || this._metrics.length <= 1) {
        // figure all this stuff out again because our _cachedMC is old
        var mcMetrics = this._cachedMC = mc.enabledMetrics();
        // we can be smart in here, and know that watts_30 means a 30
        // second moving average of watts
        var metricsByKey = {};
        for(var i = 0; i < mcMetrics.length; i++) {
          if(mcMetrics[i].indexOf('_') != -1) {
            // its a computed metric, based on a measured one
            var arr = mcMetrics[i].split('_');
            var baseMetric = arr[0], computedType = arr[1], variant = arr[2];
            if(!metricsByKey[baseMetric])
              metricsByKey[baseMetric] = this.createMetric(baseMetric);
            var computed = this.createMetric(mcMetrics[i], {
              type: computedType,
              variant: variant
            });
            metricsByKey[baseMetric].addComputed(computed);
            metricsByKey[computed.key()] = computed;
          } else {
            // regular ol metric
            if(!metricsByKey[mcMetrics[i]])
              metricsByKey[mcMetrics[i]] = this.createMetric(mcMetrics[i]);
          }
        }
        // add the x-axis metrics
        if (mc.hasDistance()) {
          metricsByKey.distance = this.createMetric('distance', { axis: 'x' });
        }
        if (mc.hasTime()) {
          metricsByKey.time = this.createMetric('time', { axis: 'x' });
        }
        this._metricsWithComputed = _.map(metricsByKey, function(m) { return m });
        this._metrics = _.filter(this._metricsWithComputed, function(m) {return !m.isComputed();});
        this._metricsByKey = metricsByKey;
      }
      // if they specified a key, give them that metric
      if(key == 'all') return this._metricsWithComputed;
      else if(key) return this._metricsByKey[key];
      // otherwise give them the array
      return this._metrics || [];
    },

    // make sure we have all the data we need to render, such as
    // mins/maxes, axis labels, etc
    // all the stuff that needs to be ready before drawing can happen
    preRender: function() {
      var data = this.dataToRender();
      // make sure we have data summaries ready
      var metricsCalculator = this.metricsCalculatorForDataToRender();
      var metrics = this.metricsEnabled();
      while (metrics.indexOf('surfaces') >= 0) {
        // disregard 'surfaces' for purposes of plot dimension calculations
        metrics.remove(metrics.indexOf('surfaces'), 1)
      }
      // we'll alternate sides for each y-axis, left/right/left/right
      // with that in mind... compute space needed and set padding
      this.yAxisWidth = 40;
      this.plotMargin({
        left: Math.ceil(metrics.length / 2) * this.yAxisWidth,
        right: Math.max(10, Math.floor(metrics.length / 2) * this.yAxisWidth),
        bottom: this.xAxisOuterHeight()
      });

      // helper function to compute all the values we need for our plotMetaData
      // these are: the plot min/max, the pixel to plotval ratio, the nice tick values, and
      // the nfrac (number of fractional decimals to show in tick labels)
      var setupPlotMetaData = _.bind(function(metricKey, min, max, ntick, tightMin, tightMax) {
        // if max and min are same, need to spread them out so we can
        // draw a y-axis
        if(max == min) {
          max += 1;
          min -= 1;
        }
        // require a minimum range difference of 45 meters for ele graph
        // otherwise a mostly flat ride can look like you're crossing the alps
        if(metricKey == 'ele' && !this.zoomedIn() && max - min < 45) {
          max += 45-(max-min)
        }
        var metric = this.metrics(metricKey),
          looseLabels = SG.looseLabels(metric.convert(min), metric.convert(max), ntick),
          numTicks = looseLabels.tickValues.length,
          looseMin = metric.inverseConvert(looseLabels.tickValues[0]),
          looseMax = metric.inverseConvert(looseLabels.tickValues[numTicks-1]),
          tickSliceStart, tickSliceEnd, pmd = this.plotMetaData[metricKey];
        if(tightMin) {
          pmd.min = min;
          tickSliceStart = 1;
        } else {
          pmd.min = looseMin;
          tickSliceStart = 0;
        }
        if(tightMax) {
          pmd.max = max;
          tickSliceEnd = numTicks - 1;
        } else {
          pmd.max = looseMax;
          tickSliceEnd = numTicks;
        }

        pmd.tickValues = looseLabels.tickValues.slice(tickSliceStart, tickSliceEnd);
        pmd.nfrac = looseLabels.nfrac;
        pmd.ratio = this.plotHeight / (pmd.max - pmd.min);
      }, this);

      // now just run through each metric and use our plotMetaData helper
      var idealYTickNum = Math.round(this.plotHeight / 20);
      for(var i=0; i < metrics.length; i++) {
        var metric = metrics[i],
          max = metricsCalculator.getMetric(metric, '_max'),
          min = metricsCalculator.getMetric(metric, '_min'),
          paddedMin = min - 0.05 * (max - min);
        setupPlotMetaData(metric, metric == 'ele' ? paddedMin : min, max, idealYTickNum, metric == 'ele', false);
      }

      // and set the plotMetaData for whatever our x-axis is
      var xAxisType = this.xAxisType();

      setupPlotMetaData(xAxisType,
                        data[0][xAxisType], // min
                        data[data.length-1][xAxisType], // max
                        Math.floor(this.plotWidth / 60), // ideal # of x-ticks
                        true, // tight min
                        true); // tight max
    },

    /**
     * Standard rendering routing.
     */
    render: function(force) {
      if(this.dataToRender().length < 2) return; // we must graph at least 2 points!
      if(force) {
        this.synchronousRender();
      } else {
        var that = this;
        if(this._renderId) {
          cancelAnimationFrame(this._renderId);
        }
        this._renderId = requestAnimationFrame(function() { that.synchronousRender(); });
      }
    },

    clearAll: function() {
      this.clearBeforeRender();
      $('.'+this.uniqueClass).remove();
    },

    /**
     * Once unload is ran, this will be set to true. Asynchronous rendering routines will respect the unloaded state and abort if the graph was unloaded before they had a chance to run.
     * @property unloaded
     * @default false
     */
    unloaded: false,

    /**
     * A destructor for the graph. Remove all DOM elements and unbind all event handlers.
     */
    unload: function() {
      this.unloaded = true;
      this.unloadBindings();
      this.clearAll();
    },

    // clear canvas and redraw all axis and plots
    synchronousRender: function() {
      if(this.unloaded) return;

      this.clearBeforeRender();
      if(this.dataToRender().length === 0) return;

      this.preRender();
      var metrics = this.metricsEnabled();
      var plotMargin = this.plotMargin();
      for(var i=0; i < metrics.length; i++) {
        var onLeft = i % 2 == 0;
        var yaxisoffset = onLeft ? (plotMargin.left - this.yAxisWidth) - i/2 * this.yAxisWidth : this.width() - (plotMargin.right - (i+1)/2 * this.yAxisWidth + this.yAxisWidth);
        this.drawYAxis(metrics[i], yaxisoffset);
        this.drawPlot(metrics[i]);
        // its cool to draw a line at 0 grade if grade is plotted... might be neat to be smarter about where this code is but ... refactor as you go i guess
        if(metrics[i] == 'grade') {
          this.drawHorizontalLine(this.plotToPixel(null, 0, 'grade'), this.strokeStyle('grade'));
        }
      }
      this.drawXAxis();
      this.trigger('reflow');
      this.trigger('render:after');
    },

    xAxisConfig: function() {
      if(!this._xAxisConfig) this._xAxisConfig = _.extend({}, SG.defaultXAxisConfig);
      return this._xAxisConfig;
    },

    xAxisOuterHeight: function() {
      var conf = this.xAxisConfig();
      return conf.lineSideMargin + conf.innerTicksLength + conf.tickLabelMargin + conf.tickLabelTextHeight + conf.axisLabelMargin + conf.axisLabelTextHeight;
    },

    // used for filtering out data
    // e.g. filters[cad] = function(x) { return x > 10 }
    filters: function(filters) {
      if(!this._filters) this._filters = {};
      if(filters) _.extend(this._filters, filters);
      return this._filters;
    },

    // grab a zIndex. things appear at different layers, and we want increasing zIndexes at a given layer each time this is called.
    zIndex: function(level) {
      if(!this._nextZIndex) {
        this._nextZIndex = 2000;
      }
      return this._nextZIndex++ + (level ? level * 1000 : 0);
    },

    formatNumber: function(x, metric, increasePrecision) {
      if(metric == 'time') {
        if(this.plotMetaData[metric].nfrac > 0 || increasePrecision) {
          // we need to display seconds, so just use the hh:mm:ss format
          return rwgps.LabelMaker.durationLabel(Math.round(x*60));
        } else {
          // no need to show seconds so use the xhym format
          return rwgps.LabelMaker.durationLabel(Math.round(x*60), true);
        }
      } else {
        var nfrac = this.plotMetaData[metric].nfrac;
        if(increasePrecision) nfrac += 1;
        if(nfrac == 0 && x > 990 && rwgps.site != 'gi') {
          return Math.round(x * 0.01)/10 + 'k';
        } else {
          return x.toFixed(nfrac);
        }
      }
    },

    dataSlice: function(i, count) {
      return new rwgps.DataSlice(this, i, count);
    },

    createMetric: function(key, opts) {
      return new SG.Metric(this, key, opts);
    },

    partitionByDistanceFraction: function(frac) {
      this.partition(this._data.partitionByDistanceFraction(frac));
    },
    partition: function(partition) {
      if(partition !== undefined) {
        if(this.zoomedIn()) this.zoomOutOnClick();
        this._partition = partition;
        this.trigger('partition:change', partition);
      }
      return this._partition;
    },
    surfacePattern: function() {
      return this.ctx.createPattern(this.unpavedPattern(), 'repeat');
    }
  });

  SG.niceNum = function(x, round) {
    var exp, f, nf;
    exp = Math.floor(Math.log(x) / Math.LN10);
    f = x / Math.pow(10, exp);
    if(round) {
      if(f < 1.5) nf = 1;
      else if(f < 3) nf = 2;
      else if(f < 7) nf = 5;
      else nf = 10;
    } else {
      if(f <= 1) nf = 1;
      else if(f <= 2) nf = 2;
      else if(f <= 5) nf = 5;
      else nf = 10;
    }
    return nf * Math.pow(10, exp);
  }

  SG.looseLabels = function(min, max, ntick) {
    var labelsAt = [],
      nfrac, d, graphmin, graphmax, range, x;
    // prevent non-zero values from hitting the graph bottom, otherwise looks like dropouts
    if(min != 0) {
        var newmin = min - (max-min) * 0.005;
        // if our actual min is greater than 0, and the padded min ends up dropping below 0, just set it as 0
        // it's most likely something that has a natural min of 0 and just drops low (speed, cad, watts, etc)
        if(min > 0 && newmin < 0)
          min = 0;
        else
          min = newmin;
    }
    range = SG.niceNum(max - min, false);
    d = SG.niceNum(range / (ntick - 1), true);
    graphmin = Math.floor(min / d) * d;
    graphmax = Math.ceil(max / d) * d;
    nfrac = Math.max(- Math.floor(Math.log(d) / Math.LN10), 0);
    for(x = graphmin; x <= graphmax + 0.5*d; x += d) {
      labelsAt.push(x);
    }
    return {tickValues: labelsAt, nfrac: nfrac};
  };

  SG.Metric = function(graph, key, opts) {
    this._graph = graph;
    this._key = key;
    this._opts = opts || {};
    // set up conversion and inverse conversion functions
    if(key == 'time') {
      // our "conversion" for time is just offsetting by first value
      var firstTimeValue = this._graph.data()[0].time;
      this.convert = function(x) { return (x - firstTimeValue)/60 }
      this.inverseConvert = function(x) { return x*60 + firstTimeValue }
    } else {
      this.convert = SG.defaultUnitConversions[key] || SG.identityFn;
      this.inverseConvert = SG.defaultInverseUnitConversions[key] || SG.identityFn;
    }
    // initialize some private vars
    this._computed = [];
    this._axis = this._opts.axis || 'y';
  }
  SG.Metric.prototype = {
    key: function() { return this._key },
    computed: function() {
      return this._computed;
    },
    addComputed: function(metric) {
      this._computed.push(metric);
    },
    isComputed: function() { return !!this._opts.type },
    computedType: function() { return this._opts.type },
    computedVariant: function() { return this._opts.variant },
    description: function() {
      if(this.computedType() == 'ma')
        return this.computedVariant() + 's moving avg';
      else
        return 'UNSUPPORTED'
    },
    baseMetricKey: function() {
      return this.key().split('_')[0];
    },
    axis: function() { return this._axis }
  }


  SG.defaultXAxisConfig = {
    innerTicksLength: 4,
    lineSideMargin: -1,
    tickLabelMargin: 4,
    tickLabelTextHeight: 10,
    axisLabelMargin: 8,
    axisLabelTextHeight: 10
  }

  /**
   * Default options that a new graph is configured with.
   * @class defaultOptions
   */
  SG.defaultOptions = {
    /**
     * @property showXAxisLabel
     * @default true
     */
    showXAxisLabel: true,
    /**
     * Display control bar
     * @property displayControls
     * @default true
     */
    displayControls: true,

    /**
      * Display light controls excluding
      *
      */
    displayLightControls: false,
    /**
     * Enable reference-point and reference-selection support
     */
    referenceTools: false,
    /**
     * Enable the "edit" menu for a segment selection
     */
    editMenu: true,
    /**
     * Enable the "save as" menu for a segment selection
     */
    saveMenu: true,

    enableUndoRedo: false,
    /**
     * Colors for each type of fill
     */
    fills: {
      ele: '#cf635a',
      paved: '#cf635a',
      unknown: '#FAEFEE',
      excluded: '#cccccc'
    },
    /**
     * colors for each type of stroke
     */
    strokes: {
      ele: '#ba676a',
      excluded: '#cccccc',
      hr: '#0048fb',
      grade: '#666666',
      cad: '#285c1d',
      speed: '#000000',
      watts: '#000000'
    }
  }

  /**
   * Global graph options that apply to all graphs instantiated.
   * @class globalOptions
   */
  SG.globalOptions = {
    /**
     * Delegate zooming to Data instance (which can be shared), allowing multiple graphs to zoom simultaneously.
     * @property globalZoom
     * @default true
     */
    globalZoom: true,
    /**
     * Listen for mousemove on any canvas
     * @property globalHighlight
     * @default true
     */
    globalHighlight: true
  }

  SG.defaultUnitConversions = {
    distance: rwgps.Convert.mToLong,
    ele: rwgps.Convert.mToShort,
    temp: rwgps.Convert.cToDeg,
    speed: rwgps.Convert.kmToLong
  }
  SG.defaultInverseUnitConversions = {
    distance: rwgps.Convert.longToM,
    ele: rwgps.Convert.shortToM,
    temp: rwgps.Convert.degToC,
    speed: rwgps.Convert.longToKm
  }

  SG.identityFn = function(x) { return x }

  // just return all the metrics we know about, in an order suitable for listing
  SG.availableMetrics = function() {
    return ['ele', 'grade', 'hr', 'cad', 'speed', 'watts'];
  }

  // TODO make this part of Metric
  SG.unitsFor = function(metric) {
    switch(metric) {
      case 'hr': return 'bpm';
      case 'cad': return 'rpm';
      case 'watts': return 'watts';
      case 'grade': return 'percent';
      case 'ele': return (rwgps.LabelMaker.metric ? 'm': 'ft');
      case 'speed': return (rwgps.LabelMaker.metric ? 'kph': 'mph');
      case 'distance': return (rwgps.LabelMaker.metric ? 'km': 'miles');
      case 'temp': return (rwgps.LabelMaker.metric ? 'deg_c': 'deg_f');
    }
  }

  SG._registeredModules = {};

  SG.registerModule = function(module) {
    this._registeredModules[module] = true;
  }

  SG.isModuleLoaded = function(module) {
    return !! this._registeredModules[module];
  }

  // export to global
  module.exports = SG;
})();



// WEBPACK FOOTER //
// ./public/javascripts/sample_graph/sample_graph.js